import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { SchoolmanagementService } from './schoolmanagement.service';

export interface LocationResult {
  country: string;
  countryCode: string;
  region: string;
  city: string;
  source: 'gps' | 'ip' | 'browser-language' | 'default';
  expiry: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class DetectlocationService {
    location: LocationResult | null = null;
    locationLoading = true;
    constructor(private http: HttpClient, private schoolService: SchoolmanagementService) {
      
    }

    async getCurrencyString(){
        let regionId = await this.getRegionID();
        if(regionId){
            switch (regionId) {
                case 1:
                    return "NGN ₦";
                case 3:
                    return "CAD $";
                default:
                    return "USD $";
            }
        }
        else {
            switch (this.location.countryCode.toUpperCase()) {
                case "NG":
                    return "NGN ₦";
                case "CA":
                    return "CAD $";
                default:
                    return "USD $";
            }
        }      
    }

    async getRegionID() {
        let schoolId = await this.schoolService.getSchoolIdFromSubdomain().toPromise()
        let school: any = await this.schoolService.getSchoolDetails(schoolId).toPromise();
        if(school && school?.schoolInfo?.regionId > 0 ){
            return school?.schoolInfo?.regionId;
        }
        else {
            let country = this.location.countryCode
            switch (country.toUpperCase()) {
                case "NG":
                    return 1;
                case "CA":
                    return 3;
                default:
                    return 2;
            }
        }        
    }

    private detecting = false;

    getLocation() {
        const saved = this.getLocationFromLocalStorage();
        if (saved) {
            this.location = saved;
            this.locationLoading = false;
            return;
        }
        if (this.detecting) return;   // a lookup is already in flight — never fire a second one
        this.detecting = true;

        this.detectLocation().subscribe({
            next: (result) => this.applyResult(result),
            // detectLocation() is designed never to error; this is a last-resort guard so
            // nothing ever escapes to the console or the UI.
            error: () => this.applyResult(this.defaultLocation()),
        });
    }

    private applyResult(result: LocationResult) {
        this.location = result;
        this.locationLoading = false;
        this.detecting = false;
        try {
            localStorage.setItem('userRegion', JSON.stringify(result));
        } catch {
            // storage unavailable (private mode, quota) — keep the in-memory value
        }
    }

    private detectLocation(): Observable<LocationResult> {
        return this.tryGPS().pipe(
            catchError(() => this.tryIPGeolocation()),
            catchError(() => of(this.defaultLocation()))
        );
    }

    private getLocationFromLocalStorage(): LocationResult | null {
        try {
            const data: LocationResult = JSON.parse(localStorage.getItem('userRegion') || 'null');
            if (data && data.expiry && Date.now() < data.expiry) {
                return data;
            }
        } catch {
            // malformed or unavailable storage — treat as no saved location
        }
        return null;
    }
    
    private tryGPS(): Observable<LocationResult> {
        return new Observable(observer => {
            if (!navigator.geolocation) {
                observer.error('GPS not supported');
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude: lat, longitude: lng } = position.coords;                    
                    this.reverseGeocode(lat, lng).subscribe({
                        next: (result) => { observer.next(result); observer.complete(); },
                        error: (err) => observer.error(err)
                    });
                },
                (error) => observer.error(error),
                { 
                    timeout: 10000, 
                    maximumAge: 43200000, // cached for 12 hours
                }
            );
        });
    }

    private reverseGeocode(lat: number, lng: number): Observable<LocationResult> {
        return this.http.get<any>(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
        ).pipe(
            switchMap(response => {
                const address = response.address;
                const expiry = new Date();
                return of({
                    country: address.country || 'Unknown',
                    countryCode: address.country_code?.toUpperCase() || 'Unknown',
                    region: address.state || address.region || 'Unknown',
                    city: address.city || address.town || address.village || 'Unknown',
                    source: 'gps' as const,
                    expiry: expiry.setHours(expiry.getHours() + 12)
                });
            })
        );
    }

    private tryIPGeolocation(): Observable<LocationResult> {
        // ipwho.is: free, HTTPS, no API key, no hard daily cap.
        // A failed lookup throws; detectLocation() then falls back to defaultLocation().
        return this.http.get<any>('https://ipwho.is/').pipe(
            switchMap(data => {
                if (!data || data.success === false) throw new Error('IP lookup failed');
                const expiry = new Date();
                return of({
                    country: data.country,
                    countryCode: data.country_code,
                    region: data.region,
                    city: data.city,
                    source: 'ip' as const,
                    expiry: expiry.setHours(expiry.getHours() + 12)
                });
            })
        );
    }

    private defaultLocation(): LocationResult {
    const expiry = new Date();
    return {
       country: 'United States',
       countryCode: 'US',
       region: 'Unknown',
       city: 'Unknown',
       source: 'default',
       expiry: expiry.setHours(expiry.getHours() + 12)
    };
  }
}